import React, { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";

import { ApiEndpoints, getApiClient, getApiUrl } from "../api-http";
import { getApiLocalesForSignIn } from "../api-http/get-api-locale";
import { useLocaleContext } from "../i18n/locale-context";
import { AuthContext } from "./auth-context";
import { DecodedTokenData, UserInfo } from "./types";

export interface AuthenticatorProps extends PropsWithChildren {}

export const Authenticator: FC<AuthenticatorProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [logged, setLogged] = useState(false);
  const [userInfo, setUserInfo] = useState<DecodedTokenData>();
  const { locale } = useLocaleContext();

  useEffect(() => {
    // I am in the browser
    setChecking(true);
    getApiClient()
      .get<UserInfo>(ApiEndpoints.USER_INFO)
      .then((response) => {
        const userInfo = response.data.data;
        if (userInfo?.isLoggedIn) {
          setUserInfo(userInfo.userdata.decodeIdToken);
        }
        setLogged(userInfo?.isLoggedIn ?? false);
        return response;
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setChecking(false);
      });
  }, []);

  const authenticationRedirect = useCallback(
    (endpoint: string, redirectUrl?: string) => {
      const apiLocale = getApiLocalesForSignIn(locale);
      if (typeof window !== "undefined") {
        const url = encodeURI(
          `${getApiUrl()}/${endpoint}?redirectUri=${
            redirectUrl ?? window.location
          }&language=${apiLocale}`
        );
        window.location.assign(url);
      }
    },
    [locale]
  );

  const login = useCallback(
    ({ register = false }: { register?: boolean } = {}) => {
      const endpoint = register ? ApiEndpoints.SIGN_UP : ApiEndpoints.SIGN_IN;
      authenticationRedirect(endpoint);
    },
    [authenticationRedirect]
  );

  const logout = async () => {
    authenticationRedirect(ApiEndpoints.LOGOUT);
  };

  return (
    <AuthContext.Provider value={{ logged, checking, userInfo, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
